import { useState, useRef } from 'react';
import { formatTime, clamp } from '@/lib/utils';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  RotateCcw,
  RotateCw,
  Subtitles,
  Activity,
  PictureInPicture,
  RefreshCw,
} from 'lucide-react';
import { usePlayerStore } from '@/store/playerStore';
import type { VideoQuality } from '@/types';

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];
const QUALITIES: { label: string; value: VideoQuality; desc: string }[] = [
  { label: '1080p', value: '1080p', desc: 'Full HD (Fixed)' },
  { label: '720p', value: '720p', desc: 'HD (Smooth)' },
  { label: '480p', value: '480p', desc: 'SD (Data Saver)' },
  { label: 'Auto', value: 'auto', desc: 'Adaptive' },
];

interface PlayerControlsProps {
  onPlay: () => void;
  onPause: () => void;
  onSeek: (time: number) => void;
  onVolumeChange: (vol: number) => void;
  onMuteToggle: () => void;
  onFullscreen: () => void;
  onPiP: () => void;
  onSkip: (seconds: number) => void;
  onSubtitleToggle: () => void;
  onSpeedChange?: (speed: number) => void;
  onQualityChange?: (quality: VideoQuality) => void;
  onResync?: () => void;
  duration: number;
  onToggleTelemetry?: () => void;
  onOpenSubtitleSearch?: () => void;
}

export function PlayerControls({
  onPlay,
  onPause,
  onSeek,
  onVolumeChange,
  onMuteToggle,
  onFullscreen,
  onPiP,
  onSkip,
  onSubtitleToggle,
  onSpeedChange,
  onQualityChange,
  onResync,
  duration,
  onToggleTelemetry,
  onOpenSubtitleSearch,
}: PlayerControlsProps) {
  const {
    isPlaying,
    currentTime,
    volume,
    muted,
    playbackRate,
    isFullscreen,
    subtitleEnabled,
    subtitleCues,
    stats,
    selectedQuality,
    setSelectedQuality,
    bufferedProgress,
  } = usePlayerStore();

  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [hoverPosition, setHoverPosition] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isSpeedOpen, setIsSpeedOpen] = useState(false);
  const [isQualityOpen, setIsQualityOpen] = useState(false);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferWidth =
    bufferedProgress.bufferedTime > 0 && duration > 0
      ? Math.min(100, Math.max(progress, (bufferedProgress.bufferedTime / duration) * 100))
      : bufferedProgress.bufferedPercent > 0
      ? Math.min(100, Math.max(progress, bufferedProgress.bufferedPercent))
      : Math.min(100, progress + 8);

  const seekFromPointer = (clientX: number) => {
    if (!trackRef.current || duration <= 0) return;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    setHoverPosition(clientX - rect.left);
    setHoverTime(ratio * duration);
    onSeek(ratio * duration);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (duration <= 0) return;
    isDraggingRef.current = true;
    setIsDragging(true);
    try {
      (e.target as Element).setPointerCapture(e.pointerId);
    } catch {}
    seekFromPointer(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!trackRef.current || duration <= 0) return;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    setHoverPosition(e.clientX - rect.left);
    setHoverTime(ratio * duration);

    if (isDraggingRef.current) {
      seekFromPointer(e.clientX);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      setIsDragging(false);
      try {
        (e.target as Element).releasePointerCapture(e.pointerId);
      } catch {}
    }
  };

  const handleMouseLeave = () => {
    if (!isDraggingRef.current) {
      setHoverTime(null);
    }
  };

  const handleVolumeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    onVolumeChange(ratio);
  };

  return (
    <div className="player-controls-layer always-white dark absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent pt-12 pb-3 px-3 sm:pt-16 sm:pb-4 sm:px-6 select-none z-30">
      {/* Precision Film-Strip Scrubber */}
      <div className="relative mb-3 sm:mb-4 group py-2 -my-2 touch-none">
        {/* Floating Timecode Tooltip */}
        {hoverTime !== null && (
          <div
            className="absolute -top-7 transform -translate-x-1/2 bg-cinema-900 border border-cinema-700 text-lamp text-[11px] font-mono px-2 py-0.5 rounded shadow-lg pointer-events-none z-10"
            style={{ left: `${hoverPosition}px` }}
          >
            {formatTime(hoverTime)}
          </div>
        )}

        <div
          ref={trackRef}
          className="film-track bg-cinema-800/80 rounded-sm relative h-2 sm:h-1.5 cursor-pointer touch-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onMouseLeave={handleMouseLeave}
          role="slider"
          aria-label="Movie timeline scrubber"
          aria-valuemin={0}
          aria-valuemax={duration}
          aria-valuenow={currentTime}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') onSkip(5);
            if (e.key === 'ArrowLeft') onSkip(-5);
          }}
        >
          {/* YouTube-style RAM Buffer Progress */}
          <div
            className="absolute top-0 left-0 h-full bg-cinema-700/70 rounded-sm pointer-events-none transition-all duration-300"
            style={{ width: `${bufferWidth}%` }}
          />
          {/* Played Progress */}
          <div
            className="absolute top-0 left-0 h-full bg-lamp rounded-sm pointer-events-none"
            style={{ width: `${progress}%` }}
          />
          {/* Film Thumb */}
          <div
            className={`film-track-thumb pointer-events-none ${isDragging ? '!scale-100' : ''}`}
            style={{ left: `${progress}%` }}
          />
        </div>
      </div>

      {/* Control Deck */}
      <div className="flex items-center justify-between gap-1.5 sm:gap-4 text-xs font-mono">
        {/* Left Side: Playback and Timecode */}
        <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
          {/* 10s Rewind */}
          <button
            onClick={() => onSkip(-10)}
            className="text-cinema-300 hover:text-white transition-colors p-1.5 sm:p-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp touch-manipulation"
            title="Rewind 10 seconds (Left Arrow)"
            aria-label="Rewind 10 seconds"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          {/* Primary Play/Pause Button */}
          <button
            onClick={isPlaying ? onPause : onPlay}
            className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-lamp hover:bg-lamp-bright text-cinema-950 flex items-center justify-center transition-transform duration-150 enabled:active:scale-[0.96] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp focus-visible:ring-offset-2 focus-visible:ring-offset-cinema-950 touch-manipulation"
            title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            aria-label={isPlaying ? 'Pause movie' : 'Play movie'}
          >
            {isPlaying ? (
              <Pause className="w-4 h-4 fill-current" />
            ) : (
              <Play className="w-4 h-4 fill-current ml-0.5" />
            )}
          </button>

          {/* 10s Forward */}
          <button
            onClick={() => onSkip(10)}
            className="text-cinema-300 hover:text-white transition-colors p-1.5 sm:p-2 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp touch-manipulation"
            title="Forward 10 seconds (Right Arrow)"
            aria-label="Forward 10 seconds"
          >
            <RotateCw className="w-4 h-4" />
          </button>

          {/* Monospace Timecode */}
          <div className="flex items-center gap-1.5 text-cinema-200 font-mono tracking-tight text-[10px] sm:text-xs ml-0.5 sm:ml-2">
            <span className="text-white font-semibold">{formatTime(currentTime)}</span>
            <span className="text-cinema-500">/</span>
            <span className="text-cinema-400">{formatTime(duration)}</span>
            {bufferedProgress.bufferHealthSeconds >= 30 && (
              <span
                className="hidden sm:inline-flex items-center gap-1 text-[9px] text-emerald-400 bg-emerald-950/70 border border-emerald-800/50 px-1.5 py-0.5 rounded font-mono font-medium ml-1"
                title={`${Math.round(bufferedProgress.bufferHealthSeconds / 60)} minutes buffered ahead in RAM`}
              >
                +{Math.round(bufferedProgress.bufferHealthSeconds / 60)}m in RAM
              </span>
            )}
          </div>

          {/* Resync Button */}
          {onResync && (
            <button
              onClick={onResync}
              className="text-cinema-400 hover:text-lamp transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lamp hidden sm:block"
              title="Resync playback with room"
              aria-label="Resync playback"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Right Side: Audio, Speed, Telemetry, PiP, Subtitles, Fullscreen */}
        <div className="flex items-center gap-1.5 sm:gap-3.5">
          {/* Audio Console */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={onMuteToggle}
              className="text-cinema-300 hover:text-white transition-colors p-1.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp touch-manipulation"
              title={muted ? 'Unmute (M)' : 'Mute (M)'}
              aria-label={muted ? 'Unmute audio' : 'Mute audio'}
            >
              {muted || volume === 0 ? (
                <VolumeX className="w-4 h-4 text-reel-rose" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>
            <div
              className="w-14 sm:w-16 h-1.5 bg-cinema-800 rounded-sm cursor-pointer relative hidden sm:block"
              onClick={handleVolumeClick}
              title={`Volume: ${Math.round(volume * 100)}%`}
              role="slider"
              aria-label="Volume slider"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(volume * 100)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight' || e.key === 'ArrowUp') onVolumeChange(Math.min(1, volume + 0.1));
                if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') onVolumeChange(Math.max(0, volume - 0.1));
              }}
            >
              <div
                className="h-full bg-cinema-200 rounded-sm"
                style={{ width: `${muted ? 0 : volume * 100}%` }}
              />
            </div>
          </div>

          {/* Speed Selector */}
          <div className="relative">
            <button
              onClick={() => setIsSpeedOpen(!isSpeedOpen)}
              className="text-cinema-300 hover:text-white px-1.5 py-0.5 rounded text-[10px] sm:text-[11px] font-mono border border-cinema-700/80 hover:border-cinema-600 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lamp touch-manipulation"
              title="Playback speed"
              aria-label={`Playback speed: ${playbackRate}x`}
            >
              {playbackRate}×
            </button>
            {isSpeedOpen && (
              <div className="absolute bottom-8 right-0 bg-cinema-900 border border-cinema-700 rounded-md py-1 shadow-xl z-50 flex flex-col min-w-[65px]">
                {SPEEDS.map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      onSpeedChange?.(s);
                      setIsSpeedOpen(false);
                    }}
                    className={`px-2.5 py-1 text-left text-xs font-mono transition-colors ${
                      playbackRate === s
                        ? 'text-lamp bg-cinema-800 font-bold'
                        : 'text-cinema-300 hover:bg-cinema-800 hover:text-white'
                    }`}
                  >
                    {s}×
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quality Selector & Telemetry */}
          <div className="relative">
            <button
              onClick={() => setIsQualityOpen(!isQualityOpen)}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-cinema-900 border border-cinema-700/80 hover:border-cinema-600 text-[10px] sm:text-[11px] font-mono text-cinema-300 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lamp touch-manipulation"
              title="Stream resolution & quality settings"
              aria-label={`Stream quality: ${selectedQuality}`}
            >
              <Activity className="w-3 h-3 text-lamp" />
              <span>{selectedQuality.toUpperCase()}</span>
            </button>

            {isQualityOpen && (
              <div className="absolute bottom-8 right-0 bg-cinema-900 border border-cinema-700 rounded-md py-1.5 shadow-2xl z-50 flex flex-col min-w-[140px] text-xs font-sans">
                <div className="px-2.5 py-1 text-[10px] uppercase font-bold text-cinema-400 tracking-wider hairline-b">
                  Stream Resolution
                </div>
                {QUALITIES.map((q) => (
                  <button
                    key={q.value}
                    onClick={() => {
                      setSelectedQuality(q.value);
                      onQualityChange?.(q.value);
                      setIsQualityOpen(false);
                    }}
                    className={`px-2.5 py-1.5 text-left text-xs transition-colors flex items-center justify-between ${
                      selectedQuality === q.value
                        ? 'text-lamp bg-cinema-800 font-bold'
                        : 'text-cinema-200 hover:bg-cinema-800 hover:text-white'
                    }`}
                  >
                    <span>{q.label}</span>
                    <span className="text-[10px] text-cinema-400 font-normal">{q.desc}</span>
                  </button>
                ))}
                {onToggleTelemetry && (
                  <button
                    onClick={() => {
                      setIsQualityOpen(false);
                      onToggleTelemetry();
                    }}
                    className="mt-1 pt-1.5 px-2.5 py-1 hairline-t text-left text-[11px] text-cinema-400 hover:text-white hover:bg-cinema-800 transition-colors flex items-center gap-1.5"
                  >
                    <Activity className="w-3 h-3 text-lamp" />
                    <span>Diagnostics…</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Subtitles Console */}
          <div className="relative">
            <button
              onClick={() => {
                if (subtitleCues.length === 0 && onOpenSubtitleSearch) {
                  onOpenSubtitleSearch();
                } else {
                  setIsSubMenuOpen(!isSubMenuOpen);
                }
              }}
              className={`p-1.5 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp touch-manipulation ${
                subtitleEnabled && subtitleCues.length > 0 ? 'text-lamp' : 'text-cinema-400 hover:text-white'
              }`}
              title={subtitleCues.length > 0 ? 'Subtitle Settings' : 'Search Subtitles (OpenSubtitles)'}
              aria-label="Subtitles"
            >
              <Subtitles className="w-4 h-4" />
            </button>

            {isSubMenuOpen && (
              <div className="absolute bottom-8 right-0 bg-cinema-900 border border-cinema-700 rounded-md py-1.5 px-1 shadow-xl z-50 flex flex-col min-w-[170px] text-xs">
                {subtitleCues.length > 0 && (
                  <button
                    onClick={() => {
                      onSubtitleToggle();
                      setIsSubMenuOpen(false);
                    }}
                    className="px-2.5 py-1.5 text-left text-cinema-200 hover:bg-cinema-800 hover:text-white rounded flex items-center justify-between"
                  >
                    <span>Subtitles</span>
                    <span className={`text-[10px] font-bold ${subtitleEnabled ? 'text-lamp' : 'text-cinema-500'}`}>
                      {subtitleEnabled ? 'ON' : 'OFF'}
                    </span>
                  </button>
                )}

                {onOpenSubtitleSearch && (
                  <button
                    onClick={() => {
                      onOpenSubtitleSearch();
                      setIsSubMenuOpen(false);
                    }}
                    className="px-2.5 py-1.5 text-left text-cinema-200 hover:bg-cinema-800 hover:text-white rounded flex items-center gap-2"
                  >
                    <Subtitles className="w-3.5 h-3.5 text-lamp" />
                    <span>Search OpenSubtitles…</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Picture in Picture */}
          <button
            onClick={onPiP}
            className="text-cinema-300 hover:text-white transition-colors p-1.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp hidden sm:block"
            title="Picture in Picture"
            aria-label="Toggle Picture in Picture"
          >
            <PictureInPicture className="w-4 h-4" />
          </button>

          {/* Fullscreen */}
          <button
            onClick={onFullscreen}
            className="text-cinema-300 hover:text-white transition-colors p-1.5 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp touch-manipulation"
            title="Toggle Fullscreen (F)"
            aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
          >
            {isFullscreen ? (
              <Minimize className="w-4 h-4" />
            ) : (
              <Maximize className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
